import React, { useEffect, useState, useMemo, useCallback } from "react";
import moment from "moment";
import { useDispatch, useSelector } from "react-redux";
import { fetchApplications } from "../../../store/reducers/applicationSlice";
import { showSuccess, showError } from '../../../helpers/toast';
import Navbar from "../../navbar";
import Header from "../../header";
import Pagination from "../dashboard/appPagination";
import iconSearch from "../../../assets/media/icon-search.svg";
import DatePicker from "react-datepicker";
import iconMenuDots from "../../../assets/media/icon-menu-dots.svg";
import { Link } from "react-router-dom";
import {
  createColumnHelper,
  flexRender,
  getCoreRowModel,
  useReactTable,
} from "@tanstack/react-table";

const columnHelper = createColumnHelper();

const RejectedApp = () => {
  const dispatch = useDispatch();
  const user = useSelector((state) => state.auth.user);
  const authToken = user.access_token;
  const applications = useSelector((state) => state.applications);
  const getallUserdata = useSelector((state) => state.userdata.getalluserdata);
  const [searchQuery, setSearchQuery] = useState("");
  const [searchTerm, setSearchTerm] = useState("");
  const [debouncedSearchTerm, setDebouncedSearchTerm] = useState("");
  const [searchBy, setSearchBy] = useState("applicationId");
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(10);
  const [filteredApplication, setFilteredApplication] = useState([]);
  const [downloadingIds, setDownloadingIds] = useState([]);

  const [startDate, setStartDate] = useState(null);
  const [endDate, setEndDate] = useState(null);

  const handleSearchByChange = (e) => {
    setSearchBy(e.target.value);
  };

  // debounced search
  useEffect(() => {
    const handler = setTimeout(() => {
      setDebouncedSearchTerm(searchTerm);
    }, 500);

    return () => {
      clearTimeout(handler);
    };
  }, [searchTerm]);
  const [showReasonModal, setShowReasonModal] = useState(false);
  const [selectedReason, setSelectedReason] = useState("");

  // Fetch API
  useEffect(() => {
    let queryParams = {
      state: 'rejected' // Add state filter to API call
    };

    // SECURITY: Add company_id filter at API level for company_admin
    if (user.user?.user_role === "company_admin" && user.user?.company_id) {
      queryParams.company_id = user.user.company_id;
    }
    // SECURITY: Add user_id filter at API level for employees
    else if (user.user?.user_role === "employee" && user.user?.id && !debouncedSearchTerm) {
      queryParams.user_id = user.user.id;
    }

    if (startDate) {
      queryParams.createdFrom = moment(startDate).format("YYYY-MM-DD");
    }
    if (endDate) {
      queryParams.createdTo = moment(endDate).format("YYYY-MM-DD");
    }

    // Add search parameters if debouncedSearchTerm is present
    if (debouncedSearchTerm) {
      queryParams[searchBy] = debouncedSearchTerm;
    }

    console.log('Fetching rejected applications with params:', queryParams);
    dispatch(fetchApplications({
      accessToken: authToken,
      queryParams,
      fetchAllPages: true // Fetch all pages to show all applications
    }));
  }, [authToken, dispatch, startDate, endDate, user.user?.id, user.user?.user_role, user.user?.company_id, debouncedSearchTerm, searchBy]);


  useEffect(() => {
    console.log("Raw Applications from Redux:", applications.data);
  }, [applications.data]);

  useEffect(() => {
    console.log("All Users from Redux:", getallUserdata);
  }, [getallUserdata]);

  // Merge Data + Search + Rejected Filter
  useEffect(() => {
    console.log('useEffect triggered:', {
      applicationsDataLength: applications.data?.length,
      getallUserdataLength: getallUserdata?.length,
      userData: user.user
    });

    if (applications.data?.length > 0) {
      // Debug: Check what states are in the data
      const statesInData = [...new Set(applications.data.map(app => app.state))];
      console.log('States in applications data:', statesInData);

      const mergedData = applications.data.map((appItem) => {
        // Try multiple possible property names for user_id
        const userIdFromApp = appItem.user_id || appItem.userId || appItem.user?.id;

        const userItem = getallUserdata?.find(
          (userData) => userData.id === userIdFromApp
        );

        // Use userItem.id if found, otherwise use userIdFromApp, or fallback to undefined
        const finalUserId = userItem?.id || userIdFromApp;

        if (userItem) {
          return {
            ...appItem,
            applicationId: appItem.id,
            ...userItem,
            user_id: finalUserId, // Use the final determined user_id
            userId: userItem.id,
          };
        }
        return {
          ...appItem,
          applicationId: appItem.id,
          user_id: finalUserId, // Use the final determined user_id
          company_id: appItem.company_id || user.user?.company_id // Fallback to user's company
        };
      });

      console.log('Merged data sample:', mergedData.slice(0, 2));
      console.log('User data:', {
        userId: user.user?.id,
        companyId: user.user?.company_id,
        userDataLength: getallUserdata?.length
      });

      // Debug discrepancy data
      mergedData.forEach((item, index) => {
        if (item.discrepancy || item.discrepancies) {
          console.log(`Item ${index} discrepancy:`, item.discrepancy || item.discrepancies);
          console.log(`Item ${index} discrepancy type:`, typeof (item.discrepancy || item.discrepancies));
        }
      });

      // Filter by role, company, and state
      const companyAndStateFiltered = mergedData.filter((item) => {
        const hasCorrectState = item.state === "rejected";
        const userRole = user.user?.user_role;
        const currentUserId = user.user?.id;
        const itemUserId = item.user_id || item.userId || item.user?.id;

        let shouldShow = false;

        const itemCompanyId = item.company_id || item.companyId;

        if (userRole === "company_admin") {
          // Company admin can see all applications in their company
          const hasCorrectCompany = String(itemCompanyId) === String(user.user.company_id);
          shouldShow = hasCorrectCompany && hasCorrectState;
        } else if (userRole === "employee") {
          // Employee can only see their own applications within their company
          // Compare as strings to handle type mismatches
          const isOwnApplication = String(itemUserId) === String(currentUserId);
          const hasCorrectCompany = String(itemCompanyId) === String(user.user.company_id);
          shouldShow = isOwnApplication && hasCorrectCompany && hasCorrectState;

          // Debug for employees
          if (!shouldShow && hasCorrectState) {
            console.log('Employee filter failed:', {
              itemUserId,
              currentUserId,
              itemCompanyId,
              userCompanyId: user.user.company_id,
              itemUserIdType: typeof itemUserId,
              currentUserIdType: typeof currentUserId,
              isOwnApplication: String(itemUserId) === String(currentUserId),
              hasCorrectCompany: String(itemCompanyId) === String(user.user.company_id)
            });
          }
        } else {
          // Default: filter by company (fallback)
          const hasCorrectCompany = String(itemCompanyId) === String(user.user.company_id);
          shouldShow = hasCorrectCompany && hasCorrectState;
        }

        console.log('Filtering item:', {
          id: item.id,
          company_id: item.company_id,
          user_id: itemUserId,
          userCompanyId: user.user.company_id,
          userId: currentUserId,
          userRole: userRole,
          state: item.state,
          hasCorrectState,
          shouldShow
        });

        return shouldShow;
      });

      // Then apply search filter
      const filteredData = companyAndStateFiltered.filter((item) => {
        // If server-side search is active (debouncedSearchTerm is set), 
        // rely on the API results and skip strict local text filtering.
        if (debouncedSearchTerm && (searchTerm || searchQuery)) return true;

        if (!searchQuery && !searchTerm) return true; // Show all if no search query

        const term = searchQuery || searchTerm;
        return Object.values(item).some(
          (value) =>
            value &&
            value.toString().toLowerCase().includes(term.toLowerCase())
        );
      });

      console.log('Rejected - Filtered data:', {
        originalData: applications.data?.length,
        mergedData: mergedData.length,
        companyAndStateFiltered: companyAndStateFiltered.length,
        filteredData: filteredData.length,
        searchQuery,
        companyId: user.user.company_id,
        statesInData
      });

      setFilteredApplication(filteredData);
      setCurrentPage(1); // Reset to first page when data changes
    }
  }, [searchQuery, applications.data, getallUserdata, user.user]);

  const handleSearchInputChange = (e) => {
    setSearchQuery(e.target.value);
    setSearchTerm(e.target.value);
  };

  const getTotalPages = () => {
    return Math.ceil(filteredApplication.length / itemsPerPage);
  };

  const handleDownload = useCallback(async (item) => {
    const { applicationId, userId } = item;

    // Prevent multiple clicks
    if (downloadingIds.includes(applicationId)) {
      return;
    }

    setDownloadingIds((prev) => [...prev, applicationId]);

    try {
      const response = await fetch(
        `${process.env.REACT_APP_BACKEND_URL}file/${userId}/downloadapplicationfiles/${applicationId}`,
        {
          method: "GET",
          headers: {
            "Content-Type": "application/zip",
            Authorization: `Bearer ${authToken}`,
          },
        }
      );

      if (!response.ok) {
        throw new Error(`Download failed: ${response.status} ${response.statusText}`);
      }

      const blob = await response.blob();
      const url = URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.download = `application_${applicationId}_files.zip`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);
      showSuccess('Files downloaded successfully!');
    } catch (error) {
      console.error("Download failed", error);
      showError(`Download failed: ${error.message}`);
    } finally {
      setDownloadingIds((prev) => prev.filter((id) => id !== applicationId));
    }
  }, [authToken, downloadingIds]);

  // Handle reason click to show modal
  const handleReasonClick = (discrepancies) => {
    if (typeof discrepancies === 'string') {
      // Single reason
      setSelectedReason(discrepancies || "No reason provided");
    } else if (discrepancies && typeof discrepancies === 'object') {
      // Multiple discrepancies - format them nicely
      const formattedReasons = Object.entries(discrepancies).map(([key, obj]) => {
        return `${key}: ${obj?.value || 'No value provided'}`;
      }).join('\n\n');
      setSelectedReason(formattedReasons || "No reasons provided");
    } else {
      setSelectedReason("No reason provided");
    }
    setShowReasonModal(true);
  };

  // Paginated Data
  const paginatedData = useMemo(() => {
    return filteredApplication
      .sort((a, b) => b.applicationId - a.applicationId)
      .slice((currentPage - 1) * itemsPerPage, currentPage * itemsPerPage);
  }, [filteredApplication, currentPage, itemsPerPage]);

  // Table Columns
  const columns = useMemo(
    () => [
      columnHelper.display({
        id: "serial",
        header: "S.NO.",
        cell: (info) => (currentPage - 1) * itemsPerPage + info.row.index + 1,
      }),
      columnHelper.accessor("applicationId", {
        header: "Application No.",
        cell: (info) => `GB-${info.getValue()}`,
      }),
      columnHelper.display({
        id: "applicationName",
        header: "Application Name",
        cell: (info) => {
          const row = info.row.original;
          return (
            <div className="boardTitle">
              <strong title={row.name || row.applicationName}>
                {(row.name || row.applicationName || "")?.length > 50
                  ? `${(row.name || row.applicationName || "").substring(0, 50)}...`
                  : (row.name || row.applicationName || 'N/A')}
              </strong>
              <span>{row.categoryName || row.category || 'N/A'}</span>
            </div>
          );
        },
      }),
      columnHelper.display({
        id: "submittedBy",
        header: "Submitted By",
        cell: (info) => {
          const row = info.row.original;

          return (
            <div className="boardTitle">
              <strong>{row.companyName || row.company_name || 'N/A'}</strong>
              <span>{row.companyEmail || row.email || 'N/A'}</span>
            </div>
          );
        },
      }),
      columnHelper.display({
        id: "submittedFor",
        header: "Submitted For",
        cell: (info) => {
          const row = info.row.original;
          return (
            <div className="boardTitle">
              <strong>{row.userName || row.user_name || row.name || 'N/A'}</strong>
              <span>{row.userEmail || row.user_email || row.email || 'N/A'}</span>
            </div>
          );
        },
      }),
      columnHelper.accessor("updated_at", {
        header: "Submitted Date",
        cell: (info) => (
          <span className="dateText">{info.getValue()?.slice(0, 10)}</span>
        ),
      }),
      columnHelper.display({
        id: "discrepancies",
        header: "Reason",
        cell: (info) => {
          const row = info.row.original;
          // Try both 'discrepancy' and 'discrepancies' field names
          let d = row.discrepancy || row.discrepancies;

          if (!d) {
            return "N/A";
          }

          // Try to parse if it's a JSON string
          if (typeof d === 'string') {
            try {
              d = JSON.parse(d);
            } catch (e) {
              return d; // Return the string as is
            }
          }

          // Check if discrepancy is an object with entries
          if (typeof d === 'object' && d !== null && Object.keys(d).length > 0) {
            return (
              <div>
                {Object.entries(d).map(([key, obj]) => (
                  <div
                    className="reasonText clickable-reason"
                    key={key}
                    onClick={() => handleReasonClick(d)}
                    style={{ cursor: 'pointer' }}
                  >
                    View Reason
                  </div>
                ))}
              </div>
            );
          } else {
            return "No reasons available";
          }
        },
      }),

      columnHelper.display({
        id: "actions",
        header: "Actions",
        cell: (info) => {
          const row = info.row.original;
          return (
            <div className="boardActions">
              <div className="dropdown d-inline-flex">
                <button
                  className="application-dropdown dropdown-toggle border-0 bg-transparent"
                  type="button"
                  id="dropdownMenuButton"
                  data-bs-toggle="dropdown"
                  aria-expanded="false"
                >
                  <img src={iconMenuDots} alt="Menu Dots" />
                </button>
                <ul
                  className="dropdown-menu"
                  aria-labelledby="dropdownMenuButton"
                >
                  <li>
                    <Link
                      onClick={() => {
                        if (!downloadingIds.includes(row.applicationId)) {
                          handleDownload(row);
                        }
                      }}
                      className="dropdown-item"
                      style={{
                        opacity: downloadingIds.includes(row.applicationId) ? 0.6 : 1,
                        pointerEvents: downloadingIds.includes(row.applicationId) ? 'none' : 'auto',
                        cursor: downloadingIds.includes(row.applicationId) ? 'not-allowed' : 'pointer'
                      }}
                    >
                      {downloadingIds.includes(row.applicationId) ? 'Downloading...' : 'Download'}
                    </Link>
                  </li>
                  <li>
                    <Link
                      onClick={() => handleDownload(row)}
                      className="dropdown-item"
                    >
                      Create new appllication
                    </Link>
                  </li>
                </ul>
              </div>
            </div>
          );
        },
      }),
    ],
    [downloadingIds, currentPage, itemsPerPage, handleDownload, getallUserdata]
  );

  const table = useReactTable({
    data: paginatedData,
    columns,
    getCoreRowModel: getCoreRowModel(),
  });

  // Add loading state
  if (applications.fetchLoading) {
    return (
      <div className="mainApp">
        <Navbar />
        <Header />
        <div className="wrapper">
          <div className="boxAppBoard boxWhite">
            <div className="text-center p-4">
              <div className="spinner"></div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // Show message if no data is available
  if (!applications.data || applications.data.length === 0) {
    return (
      <div className="mainApp">
        <Navbar />
        <Header />
        <div className="wrapper">
          <div className="boxAppBoard boxWhite">
            <div className="text-center p-4">
              <p>No applications data available. Please check your connection or try again later.</p>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="mainApp">
      <Navbar />
      <Header />
      <div className="wrapper">
        <div className="boxAppBoard boxWhite">
          {/* Header */}
          <div className="boardHeader">
            <div className="boardHeadLeft">
              <h2>Rejected Applications</h2>
            </div>
            <div className="boardHeadRight">
              <select onChange={handleSearchByChange} value={searchBy} >
                <option value="applicationId">Search by Application ID</option>
                <option value="username">Search by Employee Name</option>

              </select>
              <form className="searchForm">
                <input
                  type="text"
                  placeholder="Search"
                  onChange={handleSearchInputChange}
                  value={searchTerm}
                  className="searchInput"
                />
                <button type="button" className="searchBtn">
                  <img src={iconSearch} alt="search" />
                </button>
              </form>
              <div className="datePicker">
                <DatePicker
                  selectsRange={true}
                  startDate={startDate}
                  endDate={endDate}
                  onChange={(update) => {
                    const [start, end] = update;
                    setStartDate(start);
                    setEndDate(end);
                    setCurrentPage(1);
                  }}
                  isClearable={true}
                  placeholderText="Select Date Range"
                  className="date-range-input"
                  dateFormat="dd/MM/yyyy"
                />
              </div>
            </div>
          </div>

          {/* Table */}
          <div className="boardContent table-responsive">
            <table className="table">
              <thead className="">
                {table.getHeaderGroups().map((headerGroup) => (
                  <tr className="" key={headerGroup.id}>
                    {headerGroup.headers.map((header) => (
                      <th
                        key={header.id}
                        className={""}
                      >
                        {flexRender(
                          header.column.columnDef.header,
                          header.getContext()
                        )}
                      </th>
                    ))}
                  </tr>
                ))}
              </thead>

              <tbody>
                {table.getRowModel().rows.length > 0 ? (
                  table.getRowModel().rows.map((row) => (
                    <tr className="" key={row.id}>
                      {row.getVisibleCells().map((cell) => (
                        <td
                          key={cell.id}
                        >
                          {flexRender(cell.column.columnDef.cell, cell.getContext())}
                        </td>
                      ))}
                    </tr>
                  ))
                ) : (
                  <tr>
                    <td colSpan={table.getHeaderGroups()?.[0]?.headers.length} style={{ textAlign: "center", padding: "20px" }}>
                      <p>No rejected applications found for your company</p>
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>

          {/* Reason Modal */}
          {showReasonModal && (
            <>
              {/* Modal Backdrop */}
              <div
                className="modal-backdrop show"
                onClick={() => setShowReasonModal(false)}
                style={{ zIndex: 1040 }}
              ></div>

              {/* Modal */}
              <div
                className="modal show d-block"
                tabIndex="-1"
                role="dialog"
                style={{ zIndex: 1050 }}
              >
                <div className="modal-dialog reason-modal" role="document">
                  <div className="modal-content">
                    <div className="modal-header">
                      <h5 className="modal-title">Rejection Reasons</h5>
                      <button
                        type="button"
                        className="btn-close"
                        onClick={() => setShowReasonModal(false)}
                        aria-label="Close"
                      ></button>
                    </div>
                    <div className="modal-body">
                      <div style={{ whiteSpace: 'pre-line', maxHeight: '400px', overflowY: 'auto' }}>
                        {selectedReason}
                      </div>
                    </div>
                    <div className="modal-footer">
                      <button
                        type="button"
                        className="btn btn-secondary"
                        onClick={() => setShowReasonModal(false)}
                      >
                        Close
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            </>
          )}

          {/* Pagination */}
          <Pagination
            currentPage={currentPage}
            setCurrentPage={setCurrentPage}
            totalPages={getTotalPages}
            itemsPerPage={itemsPerPage}
            setItemsPerPage={setItemsPerPage}
          />
        </div>
      </div>
    </div>
  );
};

export default RejectedApp;